import type { IPublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { apiScopes } from '../auth/authConfig';

/**
 * Enterprise-grade Token Refresh Service
 * Proactively refreshes tokens before expiry to ensure uninterrupted user experience
 */
class TokenRefreshService {
    private refreshInterval: NodeJS.Timeout | null = null;
    private msalInstance: IPublicClientApplication | null = null;
    private isRefreshing = false;
    
    // Refresh tokens 5 minutes before expiry (300 seconds)
    private readonly REFRESH_BUFFER_SECONDS = 300;
    
    // Check token expiry every minute
    private readonly CHECK_INTERVAL_MS = 60 * 1000;

    /**
     * Initialize the token refresh service
     */
    initialize(msalInstance: IPublicClientApplication) {
        this.msalInstance = msalInstance;
        this.startMonitoring();
        console.info('[TokenRefresh] Service initialized');
    }

    /**
     * Start monitoring token expiry and refresh proactively
     */
    private startMonitoring() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
        }

        // Check token expiry periodically
        this.refreshInterval = setInterval(() => {
            this.checkAndRefreshTokens();
        }, this.CHECK_INTERVAL_MS);

        // Initial check
        this.checkAndRefreshTokens();
    }

    /**
     * Check token expiry and refresh if needed
     */
    private async checkAndRefreshTokens() {
        if (!this.msalInstance || this.isRefreshing) {
            return;
        }

        const account = this.msalInstance.getActiveAccount();
        if (!account) {
            return;
        }

        try {
            this.isRefreshing = true;

            // Check backend API token
            await this.checkAndRefreshToken(account, apiScopes.backend, 'Backend API');
            
            // Check Graph API token
            await this.checkAndRefreshToken(account, apiScopes.graph, 'Graph API');

        } catch (error) {
            console.error('[TokenRefresh] Error during token refresh check:', error);
        } finally {
            this.isRefreshing = false;
        }
    }

    /**
     * Check specific token expiry and refresh if needed
     */
    private async checkAndRefreshToken(
        account: AccountInfo, 
        scopes: string[], 
        tokenName: string
    ) {
        try {
            // Try to get cached token
            const cachedToken = await this.msalInstance!.acquireTokenSilent({
                scopes,
                account,
            });

            if (cachedToken?.expiresOn) {
                const expiryTime = cachedToken.expiresOn.getTime();
                const currentTime = Date.now();
                const secondsUntilExpiry = Math.floor((expiryTime - currentTime) / 1000);

                // Log token status
                console.debug(
                    `[TokenRefresh] ${tokenName} token expires in ${secondsUntilExpiry}s`
                );

                // Refresh if token expires within buffer window
                if (secondsUntilExpiry <= this.REFRESH_BUFFER_SECONDS) {
                    console.info(
                        `[TokenRefresh] Proactively refreshing ${tokenName} token ` +
                        `(expires in ${secondsUntilExpiry}s)`
                    );

                    await this.refreshTokenWithRetry(account, scopes, tokenName);

                    console.info(`[TokenRefresh] ${tokenName} token refreshed successfully`);
                }
            }
        } catch (error: any) {
            // Silent failures are acceptable - the interceptor will handle it
            if (error.name === 'InteractionRequiredAuthError') {
                console.warn(
                    `[TokenRefresh] Interaction required for ${tokenName} token refresh. ` +
                    `User will be prompted on next API call.`
                );
            } else if (error.errorCode === 'monitor_window_timeout' || error.message?.includes('timeout')) {
                console.warn(
                    `[TokenRefresh] ${tokenName} token refresh timed out. ` +
                    `This is usually temporary. User will be re-authenticated if needed.`
                );
            } else {
                console.warn(`[TokenRefresh] Failed to refresh ${tokenName} token:`, error.message);
            }
        }
    }

    /**
     * Refresh token with exponential backoff retry logic
     */
    private async refreshTokenWithRetry(
        account: AccountInfo,
        scopes: string[],
        tokenName: string,
        maxRetries: number = 2
    ): Promise<void> {
        let lastError: any;
        
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                if (attempt > 0) {
                    // Exponential backoff: 1s, 2s, 4s
                    const delayMs = Math.pow(2, attempt - 1) * 1000;
                    console.info(`[TokenRefresh] Retry ${attempt}/${maxRetries} for ${tokenName} after ${delayMs}ms`);
                    await this.delay(delayMs);
                }

                await this.msalInstance!.acquireTokenSilent({
                    scopes,
                    account,
                    forceRefresh: true, // Force refresh from Azure AD
                });
                
                return; // Success!
            } catch (error: any) {
                lastError = error;
                
                // Don't retry for interaction required errors
                if (error.name === 'InteractionRequiredAuthError') {
                    throw error;
                }
                
                // Log timeout errors specifically
                if (error.errorCode === 'monitor_window_timeout' || error.message?.includes('timeout')) {
                    console.warn(
                        `[TokenRefresh] Attempt ${attempt + 1}/${maxRetries + 1} timed out for ${tokenName}`
                    );
                }
                
                // Don't retry on last attempt
                if (attempt === maxRetries) {
                    throw error;
                }
            }
        }
        
        throw lastError;
    }

    /**
     * Delay helper for retry logic
     */
    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Force immediate refresh of all tokens
     */
    async forceRefresh(): Promise<void> {
        if (!this.msalInstance) {
            throw new Error('Token refresh service not initialized');
        }

        const account = this.msalInstance.getActiveAccount();
        if (!account) {
            throw new Error('No active account found');
        }

        console.info('[TokenRefresh] Force refreshing all tokens');

        try {
            // Refresh both tokens with retry logic
            await Promise.all([
                this.refreshTokenWithRetry(account, apiScopes.backend, 'Backend API'),
                this.refreshTokenWithRetry(account, apiScopes.graph, 'Graph API'),
            ]);

            console.info('[TokenRefresh] All tokens refreshed successfully');
        } catch (error) {
            console.error('[TokenRefresh] Failed to force refresh tokens:', error);
            throw error;
        }
    }

    /**
     * Stop monitoring token expiry
     */
    stop() {
        if (this.refreshInterval) {
            clearInterval(this.refreshInterval);
            this.refreshInterval = null;
        }
        console.info('[TokenRefresh] Service stopped');
    }

    /**
     * Get time until token expiry for a specific scope
     */
    async getTimeUntilExpiry(scopes: string[]): Promise<number | null> {
        if (!this.msalInstance) {
            return null;
        }

        const account = this.msalInstance.getActiveAccount();
        if (!account) {
            return null;
        }

        try {
            const cachedToken = await this.msalInstance.acquireTokenSilent({
                scopes,
                account,
            });

            if (cachedToken?.expiresOn) {
                const expiryTime = cachedToken.expiresOn.getTime();
                const currentTime = Date.now();
                return Math.floor((expiryTime - currentTime) / 1000);
            }
        } catch (error) {
            console.error('[TokenRefresh] Failed to get token expiry:', error);
        }

        return null;
    }
}

// Export singleton instance
export const tokenRefreshService = new TokenRefreshService();
